"use client";

import { COOKIES } from ".constants/cookies";
import { FEATURE_FLAGS } from ".constants/featureFlags";
import { GE } from ".constants/gtmEvents";
import { PATH_AUTH, PATH_BUY } from ".constants/links";
import { PORTALS } from ".constants/portals";
import undressConfig from ".constants/undress.config.json";
import {
  Badge,
  Box,
  Button,
  ButtonSetSlide,
  Icon,
  IconSVG,
  usePortalContext,
} from "@cloai/uikit";
import { includes } from "lodash";
import { useTranslations } from "next-intl";
import { FC, memo, SyntheticEvent, useCallback, useMemo } from "react";
import { useCookies } from "react-cookie";

import { InputUndressType } from "@/.gql/graphql";
import { useAiRequestInfo } from "@/components/hooks/useAiRequestInfo";
import { Status } from "@/components/user-flow/controls/Status";
import { useDropzoneSettings } from "@/components/user-flow/utils/useDropzoneSettings";
import {
  ETool,
  TUndressingFile,
  useABtestContext,
  useAutoMaskContext,
  useGTMContext,
  useProfileContext,
  useUndressContext,
} from "@/providers";
import { cn } from "@/utils/cn";

import { Position } from "./Position";
import { useButtonIcon } from "./useButtonIcon";
import { useButtonText } from "./useButtonText";
import { useClickUndress } from "./useClickUndress";

export const RenderButton: FC<{ token: string | null }> = memo(({ token }) => {
  const t = useTranslations("HomePage");
  const profile = useProfileContext();
  const { sendGTM, onSendGTM } = useGTMContext();
  const { openPortal } = usePortalContext();
  const { EmailTriggerFree, EmailTriggerVip, AAB_RedesignV1, AAB_RedesignV2 } =
    useABtestContext();
  const {
    clickSettings,
    clickUndress,
    clickUndressingFile,
    clickUndressStatus,
    initialSettings,
    isOpenCustomizeSettings,
    onReUseFaceObj,
    onReUseFaceMode,
    openCustomizeSettings,
    reUseFaceObj,
    selectedVipSettings,
    undressingCost,
    undressings,
    undressingsFiles,
    videoSettings,
  } = useUndressContext();
  const {
    isShowDraw,
    onToggleShowDraw,
    currentTool,
    setTool,
    setUndoDraw,
    setCleanDraw,
    isMaskLoading,
  } = useAutoMaskContext();

  const [cookies] = useCookies([COOKIES.TUTOR_DRAW_IS_READED]);

  const AAB_Redesign = AAB_RedesignV1 || AAB_RedesignV2;

  /**
   * Извлекаем настройки дропзоны для фото и видео.
   * - `maxFilesPhoto`: максимальное число фото, которые можно загрузить.
   * - `getRootPhotoProps`, `getInputPhotoProps`: пропсы для дропзоны фото.
   * - `getRootVideoProps`, `getInputVideoProps`: пропсы для дропзоны видео.
   */
  const { maxFilesPhoto, getRootPhotoProps, getInputPhotoProps } =
    useDropzoneSettings();

  // Иконка для кнопки раздевания
  const { undressButtonIcon, reUseFaceDiscountTag } = useButtonIcon();
  const undressButtonText = useButtonText();

  // Проверяет возможность анимации для массива ID запросов
  const { checkCanAnimate } = useAiRequestInfo();

  const isCanAnimate = useMemo(
    () =>
      FEATURE_FLAGS.IS_ANIMATION_ENABLED &&
      checkCanAnimate(clickUndress?.aiRequestIds || []),
    [checkCanAnimate, clickUndress?.aiRequestIds],
  );

  const { costume, pose, set, storyline, clip } = clickSettings;

  const {
    isInit,
    isLoading,
    isUndressing,
    isSuccess,
    isError,
    isBlur,
    isEndAllUndress,
  } = clickUndressStatus;

  const disabled =
    (!clickUndressingFile && videoSettings.videoDuration === 0) ||
    isLoading ||
    isBlur;

  /**
   * Маркер текущего режима генерации
   */
  const isVideoMode = useMemo(
    () =>
      includes(
        [InputUndressType.Aniamtion, InputUndressType.Clip],
        clickUndress?.undressType,
      ),
    [clickUndress?.undressType],
  );

  /**
   * Функция `onReUseFaceMode` определяет, подписан ли пользователь на email-рассылку,
   * если нет - вызывает попап с предложением оставить своб почту.
   * Далее в любом случае  сбрасывает генерацию.
   */
  const handleReUseFace = useCallback(
    async (event: SyntheticEvent) => {
      EmailTriggerFree && openPortal(PORTALS.SUBSCRIBES_FREE);
      EmailTriggerVip && openPortal(PORTALS.SUBSCRIBES_VIP);
      await onReUseFaceMode(event);
    },
    [EmailTriggerFree, EmailTriggerVip, onReUseFaceMode, openPortal],
  );

  const { onSignUndressImage, onSignUndressVideo, onSignUndressClip } =
    useClickUndress(token);

  const onAnimate = useCallback(() => {
    sendGTM({ event: GE.FLOW__CLICK_ANIMATE });
    openPortal(PORTALS.ANIMATE_PHOTO);
  }, [openPortal, sendGTM]);

  const onUploadInstagram = useCallback(() => {
    sendGTM({ event: GE.FLOW_START_CLICK_UPLOAD_INSTAGRAM });
    openPortal(PORTALS.ATTACH_PHOTO_BY_INSTA);
  }, [openPortal, sendGTM]);

  const handleToggleDraw = useCallback(
    (sendEvent?: boolean) => {
      if (sendEvent) {
        sendEvent && sendGTM({ event: GE.FLOW_CLICK_DRAW });
        !cookies.tutorDrawIsReaded && openPortal(PORTALS.TUTOR_DRAW);
      }
      onToggleShowDraw();
    },
    [cookies.tutorDrawIsReaded, onToggleShowDraw, openPortal, sendGTM],
  );

  const handleStartGenerate = useCallback(() => {
    if (clip !== initialSettings.clip) {
      return onSignUndressClip();
    }

    return onSignUndressImage();
  }, [clip, initialSettings.clip, onSignUndressClip, onSignUndressImage]);

  /**
   * Функция `renderButton` определяет, какую кнопку отображать,
   * исходя из текущего состояния (гость/авторизованный, монеты, статус undress, демо-режим, процесс загрузки).
   */
  const renderButton = () => {
    switch (true) {
      // Ошибка генерации
      case isError || clickUndress?.error:
        return (
          <Button
            key="button-error"
            startContent={<Icon icon="block" />}
            color="danger"
            wide
            centerText
            disabled
            // error // TODO: допилить анимацию в компоненте кнопки в UI-ките
          >
            {t("statusProcess.errorPhotoIsNotProcessed")}
          </Button>
        );

      // Обработка завершена (ГОСТЬ)
      case (isSuccess || isBlur) && clickUndress?.isGuest:
        return (
          <Button
            key="button-blur"
            href={PATH_AUTH}
            startContent={<Icon icon="key" />}
            color="primary"
            wide
            centerText
            onClick={onSendGTM(GE.UNDRESS_GUEST_TO_AUTH)}
            loading={isLoading}
          >
            {t("signUp")}
          </Button>
        );

      // Обработка завершена (АВТОРИЗОВАННЫЙ)
      case (isSuccess || isBlur) && !clickUndress?.isGuest && !isVideoMode:
        return (
          <div className="flex w-full justify-center gap-2">
            {isCanAnimate && (
              <Button
                key="button-animate"
                color="primary"
                wide
                centerText
                onClick={onAnimate}
                loading={isLoading}
                disabled={!isEndAllUndress || isBlur}
              >
                {t("animate")}
              </Button>
            )}
            {FEATURE_FLAGS.IS_REMIX_ENABLED && (
              <Badge
                content={reUseFaceDiscountTag}
                size="lg"
                color="primary"
                wide
                isInvisible={isCanAnimate}
              >
                <Button
                  key="button-reuse"
                  color="secondary"
                  wide
                  centerText
                  onClick={handleReUseFace}
                  loading={isLoading}
                  disabled={!isEndAllUndress || isBlur}
                >
                  {t("remix")}
                </Button>
              </Badge>
            )}
          </div>
        );

      // Идёт загрузка (`isLoading`)
      case isLoading: {
        const text = isMaskLoading ? "processingMask" : "processingPhotos";
        return (
          <Button
            key="button-loading"
            startContent={<Icon icon="hourglass_top" />}
            wide
            centerText
            loading
          >
            {t(text)}
          </Button>
        );
      }

      // Идёт генерация
      case isUndressing:
        return (
          <Box center wide>
            <Status undress={clickUndress} />
          </Box>
        );

      /**
       * Старт флоу если:
       * - загружена фотка
       * - еще НЕ открыты доп.настроки
       * - сэты НЕ выбраны
       * - авторизован
       * - ВКЛЮЧЕН РЕЖИМ РИСОВАНИЯ
       */
      case isInit &&
        undressingsFiles.length > 0 &&
        !isOpenCustomizeSettings &&
        set === initialSettings.set &&
        storyline === initialSettings.storyline &&
        !!profile &&
        isShowDraw:
        return (
          <div className="flex w-full gap-2">
            <ButtonSetSlide
              activeButton={currentTool === ETool.BRUSH ? 0 : 1}
              color="orange"
            >
              <Button
                key="button-brush"
                isIconOnly
                variant="light"
                color="primary"
                size="md"
                onClick={() => setTool(ETool.BRUSH)}
                className="rounded-xl"
              >
                <Icon
                  icon="brush"
                  className={cn(
                    currentTool === ETool.BRUSH
                      ? "text-primary-foreground"
                      : "text-foreground",
                  )}
                />
              </Button>
              <Button
                key="button-rubber"
                isIconOnly
                variant="light"
                color="primary"
                size="md"
                onClick={() => setTool(ETool.RUBBER)}
                className="rounded-xl"
              >
                <Icon
                  icon="ink_eraser"
                  className={cn(
                    currentTool === ETool.RUBBER
                      ? "text-primary-foreground"
                      : "text-foreground",
                  )}
                />
              </Button>
            </ButtonSetSlide>

            <Button
              key="button-undo"
              isIconOnly
              variant="ghost"
              onClick={() => setUndoDraw(true)}
            >
              <Icon icon="undo" />
            </Button>

            <Button
              key="button-delete"
              isIconOnly
              variant="ghost"
              onClick={() => setCleanDraw(true)}
            >
              <Icon icon="close" />
            </Button>

            <Button
              key="button-save-mask"
              color="primary"
              wide
              onClick={() => handleToggleDraw()}
              className="max-w-110"
            >
              {(clickUndressingFile as TUndressingFile).mask
                ? t("save")
                : t("exit")}
            </Button>
          </div>
        );

      /**
       * Старт флоу если:
       * - загружена фотка
       * - еще НЕ открыты доп.настроки
       * - сэты НЕ выбраны
       * - авторизован
       */
      case isInit &&
        undressingsFiles.length > 0 &&
        !isOpenCustomizeSettings &&
        set === initialSettings.set &&
        storyline === initialSettings.storyline &&
        clip === initialSettings.clip &&
        !!profile:
        return (
          <div className="flex w-full gap-2">
            <Button
              key="next-button"
              startContent={<Icon icon="double_arrow" />}
              color="primary"
              wide
              centerText
              onClick={() =>
                AAB_Redesign
                  ? openPortal(PORTALS.USER_FLOW_CUSTOMIZE)
                  : openCustomizeSettings()
              }
              disabled={disabled}
            >
              {t("next")}
            </Button>

            {FEATURE_FLAGS.IS_DRAW_ENABLED &&
              costume !== "prompt" &&
              pose === initialSettings.pose && (
                <Button
                  key="button-draw"
                  isIconOnly
                  variant="ghost"
                  color="secondary"
                  onClick={() => handleToggleDraw(true)}
                  className="group/button"
                >
                  <Icon
                    icon="brush"
                    className="text-foreground group-hover/button:text-secondary-foreground"
                  />
                </Button>
              )}
          </div>
        );

      /**
       * - выбраны VIP-настройки
       * - стоимость генерации больше 0
       * - гость или нехватает VIP-коинов на минимальную генку
       */
      case selectedVipSettings &&
        undressingCost > 0 &&
        (!profile || profile?.coins! < undressConfig.undressCost):
        return (
          <Button
            key="button-upgade"
            href={PATH_BUY}
            scroll={false}
            color="secondary"
            wide
            onClick={onSendGTM(GE.FLOW_NOT_ENOUGH_COINS_BUY_VIP_COIN)}
          >
            {t("upgrade")}
          </Button>
        );

      /**
       * Старт флоу если:
       * - загруженая фотка
       * - открыты доп.настройки или пользователь - гость или выбран СЭТ
       */
      case isInit &&
        undressingsFiles.length > 0 &&
        (isOpenCustomizeSettings ||
          !profile ||
          set !== initialSettings.set ||
          storyline !== initialSettings.storyline ||
          clip !== initialSettings.clip):
        return (
          <Badge
            content={reUseFaceDiscountTag}
            size="lg"
            color="secondary"
            wide
            isInvisible={!reUseFaceObj || !undressingCost}
          >
            <Button
              key="button-undress"
              startContent={<Icon icon="double_arrow" />}
              endContent={profile && undressButtonIcon}
              color="primary"
              wide
              centerText
              onClick={handleStartGenerate}
              loading={isLoading}
              disabled={disabled}
            >
              {t("startGenerate")}
            </Button>
          </Badge>
        );

      // Старт флоу для видео
      case videoSettings.videoDuration > 0:
        return (
          <Button
            key="button-video"
            startContent={!profile && <Icon icon="checkroom" />}
            color="primary"
            wide
            centerText
            onClick={onSignUndressVideo}
            loading={isLoading}
            disabled={disabled}
          >
            {undressButtonText()}
          </Button>
        );

      // Старт флоу: ReUse Face мод
      case isInit && !!reUseFaceObj:
        return (
          <Button
            key="button-reuse-next"
            startContent={<Icon icon="double_arrow" />}
            color="primary"
            wide
            centerText
            onClick={onReUseFaceObj}
            loading={isLoading}
            disabled={
              !clickSettings.prompt && clickSettings.costume === "prompt"
            }
          >
            {t("reUseFace")}
          </Button>
        );

      // Старт флоу для фото
      case isInit:
        return (
          <div className="flex w-full gap-2">
            <Button
              key="button-upload"
              startContent={<Icon icon="attach_file" />}
              color="primary"
              wide
              centerText
              onClick={onSendGTM(GE.FLOW_START_CLICK_UPLOAD_FILE)}
              loading={isLoading}
              disabled={
                !clickSettings.prompt && clickSettings.costume === "prompt"
              }
              {...getRootPhotoProps({})}
            >
              <input {...getInputPhotoProps()} />
              {maxFilesPhoto === 1
                ? t("uploadAPhoto")
                : t.rich("uploadAPhotosMax", {
                    count: maxFilesPhoto,
                  })}
            </Button>

            {FEATURE_FLAGS.IS_INSTAGRAM_UPLOAD_ENABLED &&
              profile?.coins! > 0 && (
                <Button
                  isIconOnly
                  onClick={onUploadInstagram}
                  loading={isLoading}
                  disabled={
                    !clickSettings.prompt && clickSettings.costume === "prompt"
                  }
                >
                  <IconSVG icon="instagram" />
                </Button>
              )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderButton()}
      {
        // Если есть `undressings` и они не все завершены, отображаем `Position` (скрытый `div`),
        // Видимо для расчётов или позиционирования
        !isEndAllUndress && undressings && (
          <div className="hide">
            <Position
              isVideo={includes(
                [
                  InputUndressType.Aniamtion,
                  InputUndressType.Clip,
                  InputUndressType.Video,
                ],
                clickUndress?.undressType,
              )}
            />
          </div>
        )
      }
    </>
  );
});

RenderButton.displayName = "RenderButton";
